import { useState, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { last } from 'lodash';
import { withRouter } from 'react-router';
import { PrimaryButton } from 'ui/button';
import { AddNew16 } from 'ui/icons/aurora/16px';
import { Tooltip } from 'ui/tooltip';
import { PageHeader } from 'ui/pageHeader';
import Link from 'core/link';
import { Skeleton } from 'ui/skeleton';
import { MISSING_MANAGE_CATALOGS_PERMISSION } from 'modules/catalog/catalogConstants';
import CreateCollectionModal from 'modules/catalog/createCollectionModal';
import NavigationSidebar from 'modules/catalog/navigationSidebar';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { fetchRecentCatalogCollections } from 'modules/catalog/collections/actions';
import { fireSegmentTrackCall } from 'modules/segment/actions';
import {
  selectCatalogIsLoading,
  selectCatalogIsSubmitting,
  selectAllCatalogsTableRows,
  selectCatalogTableFilters,
  selectCatalogIsDisplayTable,
  selectCatalogNames,
  selectCatalogNamesOnlyCaseInsensitive,
} from 'modules/catalogTable/selectors';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { selectCollections } from 'modules/catalog/collections/selectors';
import { selectIsCatalogCollectionsFlagOn } from 'core/userContext';
import { DeleteCatalogModal } from './DeleteCatalogModal';
import { CreateCatalogModal } from './CreateCatalogModal';
import CatalogCollectionCTA from './CatalogCollectionCTA';
import { AllCatalogsTableButtonRow } from './AllCatalogsTableButtonRow';
import { AllCatalogsPageContent, StyledCoreTable } from './CatalogTable.styled';
import {
  ALL_CATALOGS_COLUMNS,
  SET_MODAL_STATE,
  CREATE_CATALOG_MODAL,
  CREATE_COLLECTION_MODAL,
  DELETE_MODAL,
  INITIAL_REDUCER_STATE,
} from '../constants';
import {
  changeTableColumns,
  createNewCatalogRequest,
  fetchCatalogs,
  filterTable,
  openCustomizeColumnsModal,
  removeCatalog,
} from '../actions';

const AllCatalogsTableReducer = (state, action) => {
  switch (action.type) {
    case SET_MODAL_STATE:
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

export const AllCatalogsTable = () => {
  const dispatch = useDispatch();

  const isLoading = useSelector(selectCatalogIsLoading);
  const isSubmitting = useSelector(selectCatalogIsSubmitting);
  const rowData = useSelector(selectAllCatalogsTableRows);
  const filters = useSelector(selectCatalogTableFilters) || { page: 1, pageSize: 10 };
  const isDisplayTable = useSelector(selectCatalogIsDisplayTable);
  const catalogList = useSelector(selectCatalogNames) || [];
  const catalogNames = useSelector(selectCatalogNamesOnlyCaseInsensitive);
  const isCatalogCollectionsFlagOn = useSelector(selectIsCatalogCollectionsFlagOn);
  const collections = useSelector(selectCollections) || [[], 10];
  const canManageCatalogs = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.ManageCatalogs),
  );

  const [modalState, updateModalState] = useReducer(AllCatalogsTableReducer, INITIAL_REDUCER_STATE);

  const [filteredRowData, setFilteredRowData] = useState(rowData);
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    dispatch(fetchCatalogs());
    if (isCatalogCollectionsFlagOn) {
      dispatch(fetchRecentCatalogCollections());
    }
  }, []);

  useEffect(() => {
    if (isCatalogCollectionsFlagOn) {
      dispatch(fetchRecentCatalogCollections());
    }
  }, [isCatalogCollectionsFlagOn]);

  useEffect(() => {
    setFilteredRowData(rowData);
  }, [rowData]);

  const openCreateCatalogModal = () => {
    updateModalState({
      type: SET_MODAL_STATE,
      payload: { modal: CREATE_CATALOG_MODAL, deleteName: '' },
    });
  };

  const closeModal = () => {
    updateModalState({ type: SET_MODAL_STATE, payload: { modal: '', deleteName: '' } });
  };

  const handleCreateCatalog = (catalogName) => {
    dispatch(createNewCatalogRequest(catalogName));
  };

  const openDeleteModal = (e, { name }) => {
    updateModalState({ type: SET_MODAL_STATE, payload: { modal: DELETE_MODAL, deleteName: name } });
  };

  const deleteOnClickHandler = () => {
    dispatch(removeCatalog(modalState.deleteName));
    closeModal();
  };

  const handleOpenCreateCollectionModal = () => {
    updateModalState({
      type: SET_MODAL_STATE,
      payload: { modal: CREATE_COLLECTION_MODAL, deleteName: '' },
    });
  };

  const handleChangeColumns = (e, { hiddenColumns, reordered, visibleColumns }) => {
    dispatch(changeTableColumns(visibleColumns, hiddenColumns, reordered));
  };

  const handleOpenCustomizeColumnsModal = () => {
    dispatch(openCustomizeColumnsModal());
  };

  const handleChangeTableFilters = (e, { filter }) => {
    if (filter !== undefined) {
      const filterName = filter.toLowerCase();
      const filteredData = rowData.filter((row) => row.name.toLowerCase().includes(filterName));
      dispatch(filterTable(filter));
      setFilteredRowData(filteredData);
      setFilterText(filter);
    }
  };

  const updateColsWithActions = (cols) => {
    const firstCol = {
      ...cols.slice(0, 1)[0],
      actionCell: {
        header: '',
        onClickDelete: openDeleteModal,
      },
    };
    return [firstCol, ...cols.slice(1)];
  };

  const updateRowsWithSegmentTracking = (rows) =>
    rows.map((row) => {
      const { name, id, link } = row;
      const formattedLink = (
        <Link
          onClick={() =>
            dispatch(fireSegmentTrackCall('Catalog Opened', { catalog_name: name, catalog_id: id }))
          }
          to={link}
          text={name}
          tableLinkPrimary
        />
      );
      return { ...row, col1: formattedLink };
    });

  const toolbarContent = (
    <Tooltip isEnabled={!canManageCatalogs} tooltipContent={MISSING_MANAGE_CATALOGS_PERMISSION}>
      <PrimaryButton
        data-test="openCreateCatalogModalButton"
        isDisabled={!canManageCatalogs}
        onClick={openCreateCatalogModal}
      >
        <AddNew16 />
        Create New Catalog
      </PrimaryButton>
    </Tooltip>
  );

  /**
   * Gets the empty state for the table.
   * Determines the empty state if no catalog is created or if table is filtered.
   * @returns { React.Component } The empty state.
   */
  const getEmptyStateProps = () => {
    if (filterText) {
      return {
        theme: 'light',
        text: 'No catalogs found',
      };
    }

    return {
      header: 'No catalogs',
      inline: true,
      theme: 'light',
      text: 'Create a catalog to get started',
    };
  };

  const collectionsLength = last(collections);
  const showCollectionCTA = collectionsLength === 0 && catalogList.length > 0;
  const buttonRowContent = (
    <AllCatalogsTableButtonRow
      canManageCatalogs={canManageCatalogs}
      isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
      openCreateCollectionModal={handleOpenCreateCollectionModal}
    />
  );

  const rows = updateRowsWithSegmentTracking(filteredRowData);
  const total = rows.length;

  return (
    <div>
      <CreateCatalogModal
        isOpen={modalState.modal === CREATE_CATALOG_MODAL}
        closeModal={closeModal}
        isSubmitting={isSubmitting}
        onCreate={handleCreateCatalog}
        catalogNames={catalogNames}
      />
      <DeleteCatalogModal
        onDelete={deleteOnClickHandler}
        isOpen={modalState.modal === DELETE_MODAL}
        closeModal={closeModal}
        name={modalState.deleteName}
      />
      {isCatalogCollectionsFlagOn && (
        <CreateCollectionModal
          isOpen={modalState.modal === CREATE_COLLECTION_MODAL}
          closeModal={closeModal}
        />
      )}
      <PageHeader pageActions={buttonRowContent}>Catalogs</PageHeader>
      {isCatalogCollectionsFlagOn && showCollectionCTA && (
        <CatalogCollectionCTA openCreateCollectionModal={handleOpenCreateCollectionModal} />
      )}
      {isLoading ? (
        <Skeleton height={600} />
      ) : (
        <AllCatalogsPageContent>
          {isCatalogCollectionsFlagOn && <NavigationSidebar />}
          <StyledCoreTable
            columns={
              canManageCatalogs ? updateColsWithActions(ALL_CATALOGS_COLUMNS) : ALL_CATALOGS_COLUMNS
            }
            rows={rows}
            dockLeftColumn={isDisplayTable}
            expandColumn={!isDisplayTable}
            onChangeSort={handleChangeTableFilters}
            onChangeFilter={handleChangeTableFilters}
            onChangeVisibleColumns={handleChangeColumns}
            onOpenCustomizeColumnsModal={handleOpenCustomizeColumnsModal}
            sorting={filters.sorting}
            emptyStateProps={getEmptyStateProps()}
            pageSizeInToolbar={false}
            hidePagination
            toolbarContent={toolbarContent}
            total={total}
            showFilterInput
            showToggleColumns
          />
        </AllCatalogsPageContent>
      )}
    </div>
  );
};

export default withRouter(AllCatalogsTable);
